import React from 'react';
import { 
  useRoomContext,
  RoomAudioRenderer
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import ScreenShareButton from './ScreenShareButton';

/**
 * Custom control bar with left/right clustering of controls
 */
export default function CustomControlBar({ 
  onToggleChat, 
  onChangeBackground, 
  isBackgroundEnabled, 
  audioState, 
  onToggleAudio, 
  onToggleMute
}) {
  const { localParticipant } = useRoomContext();
  const room = useRoomContext();
  
  // Handle audio toggle
  const toggleMicrophone = async () => {
    const tracks = localParticipant?.getTracks() || [];
    const audioTrack = tracks.find(track => track.source === Track.Source.Microphone);
    if (audioTrack) {
      if (audioTrack.isMuted) {
        await audioTrack.unmute();
      } else {
        await audioTrack.mute();
      }
    }
  };
  
  // Handle video toggle
  const toggleCamera = async () => {
    const tracks = localParticipant?.getTracks() || [];
    const videoTrack = tracks.find(track => track.source === Track.Source.Camera);
    if (videoTrack) {
      if (videoTrack.isMuted) {
        await videoTrack.unmute();
      } else {
        await videoTrack.mute();
      }
    }
  };
  
  // Get track states
  const tracks = localParticipant?.getTracks() || [];
  const audioTrack = tracks.find(track => track.source === Track.Source.Microphone);
  const videoTrack = tracks.find(track => track.source === Track.Source.Camera);
  const isAudioEnabled = audioTrack ? !audioTrack.isMuted : false;
  const isVideoEnabled = videoTrack ? !videoTrack.isMuted : false;

  return (
    <div className="custom-control-bar">
      <div className="control-bar-content">
        <div className="left-controls">
          {/* Microphone Button */}
          <button 
            className={`control-button ${isAudioEnabled ? 'active' : ''}`}
            onClick={toggleMicrophone}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isAudioEnabled ? (
                <>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </>
              ) : (
                <>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .95-.19 1.85-.52 2.67"></path>
                  <line x1="12" y1="19" x2="12" y2="23"></line>
                  <line x1="8" y1="23" x2="16" y2="23"></line>
                </>
              )}
            </svg>
          </button>
          
          {/* Camera Button */}
          <button 
            className={`control-button ${isVideoEnabled ? 'active' : ''}`}
            onClick={toggleCamera}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isVideoEnabled ? (
                <>
                  <polygon points="23 7 16 12 23 17 23 7"></polygon>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                </>
              ) : (
                <>
                  <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </>
              )}
            </svg>
          </button>
          
          <ScreenShareButton />
          
          {/* Leave Button */}
          <button 
            className="control-button leave-button"
            onClick={() => room.disconnect()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
        
        <div className="right-controls">
          {/* Audio Button - Only shown when background has audio */}
          {audioState?.hasAudio && (
            <>
              <button 
                className={`control-button audio-button ${!audioState.isMuted ? 'active' : ''}`}
                onClick={onToggleAudio}
                title="Background Audio Controls"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {audioState.isMuted ? (
                    <>
                      <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                      <line x1="23" y1="9" x2="17" y2="15"></line>
                      <line x1="17" y1="9" x2="23" y2="15"></line>
                    </>
                  ) : (
                    <>
                      <path d="M11 5L6 9H2v6h4l5 4V5z"></path>
                      <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                    </>
                  )}
                </svg>
                {!audioState.isMuted && (
                  <span className="status-indicator"></span>
                )}
              </button>
              {/* Explicit background audio mute/unmute toggle button */}
              <button 
                className={`control-button audio-toggle ${!audioState.isMuted ? 'active' : ''}`}
                onClick={onToggleMute}
                title={audioState.isMuted ? 'Enable Background Audio' : 'Disable Background Audio'}
              >
                {audioState.isMuted ? '🔇' : '🔊'}
              </button>
            </>
          )}
          
          {/* Background Change Button */}
          <button 
            className={`control-button ${isBackgroundEnabled ? 'active' : ''}`}
            onClick={onChangeBackground}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          </button>
          
          
          {/* Chat Button */}
          <button 
            className="control-button chat-button"
            onClick={onToggleChat}
            title="Toggle Chat Panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <RoomAudioRenderer />
      
      <style>{`
        .custom-control-bar {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          padding: 16px;
          z-index: 10;
        }
        
        .control-bar-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background-color: rgba(0, 0, 0, 0.5);
          border-radius: 8px;
          padding: 8px 16px;
          backdrop-filter: blur(10px);
          width: 100%;
          max-width: 800px;
        }
        
        .left-controls, .right-controls {
          display: flex;
          gap: 8px;
        }
        
        .control-button {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          padding: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          position: relative;
        }
        
        .control-button:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        .control-button.active {
          color: rgba(0, 255, 0, 0.7);
          border-color: rgba(0, 255, 0, 0.3);
        }
        
        .chat-button:hover {
          color: rgba(255, 255, 255, 1);
          border-color: rgba(255, 255, 255, 0.6);
          background-color: rgba(255, 255, 255, 0.1);
        }

        .audio-toggle {
          font-size: 20px;
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 4px;
          padding: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          width: 40px;
          height: 40px;
          position: relative;
        }
        .audio-toggle.active {
          color: rgba(74, 158, 255, 1);
          border-color: rgba(74, 158, 255, 0.5);
          background-color: rgba(74, 158, 255, 0.2);
        }
      `}</style>
    </div>
  );
}
